import React, { useMemo } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import styles from '../styles/profile-header.module.scss'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/reducers'
import { TabNavigationModule, TabContent, TabObj } from 'components/generic/TabNavigation'
import SubmitScorePanel from '../social-matches/panels/SubmitScorePanel'
import MyScoresPanel from '../social-matches/panels/MyScoresPanel'
import MatchResultListings, { ResultListingDropdown } from '../competitions/MatchResultListings'
import { MY_SOCIAL_MATCHES_KEY, Player, ResultsSection } from 'types/global'
import ScoresPanel from '../social-matches/panels/ScoresPanel'
import { useGuidedTour } from 'hooks/use-guided-tour'
import { useOnboardingSteps } from '../onboarding-tour/ProfileSteps'
import { WtnPages } from 'constants/guided-tours'
import withInfoModal from 'components/generic/hoc/withInfoModal'
import LockedFeature from 'components/generic/LockedFeature'
import { useResultsTabNavigation } from '../hooks'

const MyMatchesHeader = () => {
  const { t: tMatchResults } = useTranslation('strMatchResults')
  return <h1 className={styles.tabContentTitle}>{tMatchResults('social.title')}</h1>
}
const MyMatchesHeaderWithModal = withInfoModal('my-matches-info-modal', MyMatchesHeader)

const CompetitionsHeader = () => {
  const { t: tMatchResults } = useTranslation('strMatchResults')
  return <h1 className={styles.tabContentTitle}>{tMatchResults('competitions.title')}</h1>
}
const CompetitionsHeaderWithModal = withInfoModal('competitions-info-modal', CompetitionsHeader)

const BlurredMatchResults = () => {
  const placeholders = useMemo(() => {
    const date = new Date().toDateString()
    const generateName = (name: string) => {
      let index = Math.floor(Math.random() * (name.length - 3)) + 3
      if (index > name.length - 1) index = name.length - 1
      return name.slice(0, index) + ' ' + name.slice(index + 1)
    }

    return new Array(4).fill(true).map(() => ({
      title: generateName('Tournamentname'),
      tag: 'TAG',
      start: date,
      end: date,
    }))
  }, [])

  return (
    <LockedFeature info={{ ns: 'strMessageScreen', transKey: 'lockedFeature.blurredResults' }}>
      <div className={styles.blurredMatches}>
        {placeholders.map(({ title, tag, start, end }, i) => (
          <ResultListingDropdown
            key={i}
            selected={false}
            provider={{ name: '', orgId: '', providerId: '', displayName: tag }}
            title={title}
            start={start}
            end={end}
          />
        ))}
      </div>
    </LockedFeature>
  )
}

interface Props {
  player: Player
  canRunTour?: boolean
  enableSelfSubmission?: boolean
  hideResultsForAnonUser?: boolean
}

const ResultsTab = ({
  player,
  canRunTour,
  enableSelfSubmission = false,
  hideResultsForAnonUser = false,
}: Props) => {
  const { t: tProfile } = useTranslation('strProfile')
  const { t: tMatchResults } = useTranslation('strMatchResults')
  const { t: tCommonUI } = useTranslation('strCommonUI')
  const { selfSubmitSteps, selfSubmitStepsOwnProfile } = useOnboardingSteps()
  const isLoggedIn = useSelector((state: AppState) => state.user.isLoggedIn)
  const ownTennisId = useSelector((state: AppState) => state.user.tennisId)
  const hasPendingMatches = useSelector(
    (state: AppState) =>
      !!state.socialMatches.totals[MY_SOCIAL_MATCHES_KEY]?.PENDING &&
      ownTennisId === player.tennisID
  )
  const isSelfSubmissionTabVisible = enableSelfSubmission && !!player.eligibleForSelfSubmission
  const { activeSection, handleTabSelect } = useResultsTabNavigation({ isSelfSubmissionTabVisible })

  const isOwnProfile = isLoggedIn && !!ownTennisId && ownTennisId === player.tennisID
  const hideMatchResults = hideResultsForAnonUser && !isLoggedIn
  const featureTourSteps = useMemo(
    () => (isOwnProfile ? selfSubmitStepsOwnProfile : selfSubmitSteps),
    [isOwnProfile, selfSubmitSteps, selfSubmitStepsOwnProfile]
  )

  const NAV_TABS: TabObj[] = useMemo(() => {
    let tabs: TabObj[] = []

    if (isSelfSubmissionTabVisible) {
      tabs = [
        {
          id: ResultsSection.COMPETITIONS,
          key: ResultsSection.COMPETITIONS,
          title: tProfile('sections.competitions'),
          className: 'competitions-tab',
        },
        {
          id: ResultsSection.SOCIAL_MATCHES,
          key: ResultsSection.SOCIAL_MATCHES,
          title: tProfile('sections.socialMatches'),
          className: 'my-matches-tab',
          hasNotification: hasPendingMatches,
        },
      ]
    }

    return tabs
  }, [hasPendingMatches, isSelfSubmissionTabVisible, tProfile])

  const { GuidedTour, props } = useGuidedTour(
    ownTennisId,
    WtnPages.PLAYER_PROFILE,
    featureTourSteps
  )

  return (
    <>
      {isLoggedIn && canRunTour && <GuidedTour {...props} />}
      <TabNavigationModule
        navId={'results-tabs'}
        activeTabKey={activeSection}
        tabs={NAV_TABS}
        onTabSelect={handleTabSelect}
        navColorScheme='secondary'
        containerClass={styles.tabContentWrapper}
        navClass={styles.tabNavResults}
        withArrows
      >
        <TabContent tabKey={ResultsSection.COMPETITIONS} activeTabKey={activeSection}>
          <div
            className={
              isSelfSubmissionTabVisible
                ? styles.subTabContentWrapper
                : styles.subTabContentWrapperNoPadding
            }
          >
            {!isSelfSubmissionTabVisible ? (
              <h1 className={styles.tabContentTitle}>{tMatchResults('matchResults')}</h1>
            ) : isOwnProfile ? (
              <CompetitionsHeaderWithModal
                widgetElId='player-profile'
                modalHeaderText={tMatchResults('competitions.modal.header')}
                modalAcceptBtnText={tCommonUI('button.okGotIt')}
                wrapperClass={styles.titleWrapper}
                triggerClass={styles.infoModalTrigger}
              >
                <Trans ns='strProfile' i18nKey={'modals.info.competitions'}>
                  <p>
                    Under the 'Competitions' feature, all competition match results that have been
                    submitted by your National or Regional Association, or the ITF, will be shown
                    here.
                  </p>
                  <p className={styles.mb0}>
                    Note that you will be unable to submit tournament match results here.
                  </p>
                </Trans>
              </CompetitionsHeaderWithModal>
            ) : (
              <CompetitionsHeader />
            )}
            {hideMatchResults ? (
              <BlurredMatchResults />
            ) : (
              <MatchResultListings playerId={player.id} tennisId={player.tennisID} />
            )}
          </div>
        </TabContent>
        {isSelfSubmissionTabVisible ? (
          <TabContent tabKey={ResultsSection.SOCIAL_MATCHES} activeTabKey={activeSection}>
            <div className={styles.subTabContentWrapper}>
              {isOwnProfile ? (
                <>
                  <MyMatchesHeaderWithModal
                    widgetElId='player-profile'
                    modalHeaderText={tMatchResults('social.modal.header')}
                    modalAcceptBtnText={tCommonUI('button.okGotIt')}
                    wrapperClass={styles.titleWrapper}
                    triggerClass={styles.infoModalTrigger}
                  >
                    <Trans ns='strProfile' i18nKey={'modals.info.myMatches'}>
                      <p>
                        Under the ‘My Matches’ feature, you can now post scores for the matches you
                        played outside of tournaments.
                      </p>{' '}
                      <p>
                        Note that you will only be able to submit a result against a player who
                        holds an <b>ITF World Tennis Number</b> account, however this does exclude
                        IPIN Juniors and Professionals.
                      </p>{' '}
                      <p className={styles.mb0}>
                        Once a score is submitted, your opponent will be sent an email asking for
                        them to confirm the match result(s). If there is not a response within 72
                        hours, this will be automatically verified.
                      </p>
                    </Trans>
                  </MyMatchesHeaderWithModal>
                  <SubmitScorePanel />
                  <MyScoresPanel />
                </>
              ) : (
                <>
                  <MyMatchesHeader />
                  {hideMatchResults ? <BlurredMatchResults /> : <ScoresPanel player={player} />}
                </>
              )}
            </div>
          </TabContent>
        ) : (
          <></>
        )}
      </TabNavigationModule>
    </>
  )
}

export default ResultsTab
